import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Location } from '@angular/common';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

interface AccountField {
	key: string;
	label: string;
	short: string;
	group: 'sales' | 'purchase' | 'inventory';
	hint?: string;
}

interface GlOption {
	code: string;
	name: string;
	label: string;
}

@Component({
	selector: 'app-general-posting-setup',
	templateUrl: './general-posting-setup.component.html',
	styleUrls: ['./general-posting-setup.component.scss'],
})
export class GeneralPostingSetupComponent implements OnInit {
	form: FormGroup;
	rows: any[] = [];
	glOptions: GlOption[] = [];
	glIndex: Map<string, GlOption> = new Map();
	filtered: { [key: string]: Observable<GlOption[]> } = {};
	editingRow: any = null;
	isLoading = false;
	color = 'blue';
	isFormVisible = false;
	searchTerm = '';
	activeGroup: 'sales' | 'purchase' | 'inventory' = 'sales';
	copySourceCtrl: FormControl = new FormControl('');
	filteredCopySources: Observable<any[]>;
	lastCopiedFrom: { bus: string; prod: string } | null = null;

	readonly accountFields: AccountField[] = [
		{ key: 'sales_account', label: 'Sales', short: 'Sales', group: 'sales' },
		{ key: 'sales_credit_memo_account', label: 'Sales credit memo', short: 'Sales CM', group: 'sales' },
		{ key: 'sales_line_disc_account', label: 'Sales line discount', short: 'Line Disc.', group: 'sales' },
		{ key: 'sales_inv_disc_account', label: 'Sales invoice discount', short: 'Inv. Disc.', group: 'sales' },
		{ key: 'sales_pmt_tol_debit_account', label: 'Sales pmt tolerance debit', short: 'Pmt Tol. Dr', group: 'sales' },
		{ key: 'sales_pmt_tol_credit_account', label: 'Sales pmt tolerance credit', short: 'Pmt Tol. Cr', group: 'sales' },
		{ key: 'purch_account', label: 'Purchases', short: 'Purchases', group: 'purchase' },
		{ key: 'purch_credit_memo_account', label: 'Purchase credit memo', short: 'Purch CM', group: 'purchase' },
		{ key: 'purch_line_disc_account', label: 'Purchase line discount', short: 'Line Disc.', group: 'purchase' },
		{ key: 'purch_inv_disc_account', label: 'Purchase invoice discount', short: 'Inv. Disc.', group: 'purchase' },
		{ key: 'purch_pmt_tol_debit_account', label: 'Purchase pmt tolerance debit', short: 'Pmt Tol. Dr', group: 'purchase' },
		{ key: 'purch_pmt_tol_credit_account', label: 'Purchase pmt tolerance credit', short: 'Pmt Tol. Cr', group: 'purchase' },
		{ key: 'purch_prepayments_account', label: 'Purchase prepayments', short: 'Prepayments', group: 'purchase' },
		{ key: 'purch_account_allocation', label: 'Purchase allocation', short: 'Allocation', group: 'purchase' },
		{ key: 'cogs_account', label: 'COGS', short: 'COGS', group: 'inventory' },
		{ key: 'cogs_account_interim', label: 'COGS interim', short: 'COGS Interim', group: 'inventory' },
		{ key: 'inventory_adjmt_account', label: 'Inventory adjustment', short: 'Inv. Adj.', group: 'inventory' },
		{ key: 'invt_accrual_interim_account', label: 'Inventory accrual interim', short: 'Accr. Interim', group: 'inventory' },
		{ key: 'direct_cost_applied_account', label: 'Direct cost applied', short: 'Direct Cost', group: 'inventory' },
		{ key: 'overhead_applied_account', label: 'Overhead applied', short: 'Overhead', group: 'inventory' },
		{ key: 'purchase_variance_account', label: 'Purchase variance', short: 'Purch. Var.', group: 'inventory' },
	];

	readonly groupTabs: { id: 'sales' | 'purchase' | 'inventory'; label: string; icon: string }[] = [
		{ id: 'sales', label: 'Sales', icon: 'point_of_sale' },
		{ id: 'purchase', label: 'Purchases', icon: 'shopping_cart' },
		{ id: 'inventory', label: 'Inventory & COGS', icon: 'inventory_2' },
	];

	constructor(
		private fb: FormBuilder,
		private glService: GeneralLedgerService,
		private snackBar: MatSnackBar,
		private location: Location,
		private globalSearchService: GlobalSearchService,
	) {
		this.color = this.globalSearchService.getColor();
		const controls: Record<string, unknown> = {
			gen_bus_posting_group: ['', Validators.required],
			gen_prod_posting_group: ['', Validators.required],
			description: [''],
			blocked: [false],
		};
		for (const f of this.accountFields) {
			controls[f.key] = ['0', Validators.required];
		}
		this.form = this.fb.group(controls);
		this.filteredCopySources = this.copySourceCtrl.valueChanges.pipe(
			startWith(''),
			map((value) => this.filterCopySources(value)),
		);
	}

	ngOnInit(): void {
		this.loadRows();
		this.loadGlCodes();
	}

	private rowKey(row: any): string {
		if (!row) return '';
		return (row.gen_bus_posting_group || '') + '|' + (row.gen_prod_posting_group || '');
	}

	private filterCopySources(value: any): any[] {
		const editingKey = this.editingRow ? this.rowKey(this.editingRow) : '';
		const term = (value == null ? '' : String(value)).toLowerCase().trim();
		return this.rows
			.filter((r) => this.rowKey(r) !== editingKey)
			.filter((r) => {
				if (!term) return true;
				const haystack = [r.gen_bus_posting_group, r.gen_prod_posting_group, r.description]
					.map((v) => (v == null ? '' : String(v).toLowerCase()))
					.join(' ');
				return haystack.includes(term);
			})
			.slice(0, 30);
	}

	displayCopySource = (row: any): string => {
		if (!row) return '';
		if (typeof row === 'string') return row;
		const desc = row.description ? ' — ' + row.description : '';
		return row.gen_bus_posting_group + ' · ' + row.gen_prod_posting_group + desc;
	};

	copyAccountsFrom(row: any): void {
		if (!row || typeof row !== 'object') {
			return;
		}
		for (const f of this.accountFields) {
			const v = row[f.key];
			this.form.get(f.key)?.setValue(v != null && String(v) !== '' ? String(v) : '0');
		}
		this.lastCopiedFrom = {
			bus: row.gen_bus_posting_group,
			prod: row.gen_prod_posting_group,
		};
		this.copySourceCtrl.setValue('', { emitEvent: false });
		this.snackBar.open(
			'Copied accounts from ' + row.gen_bus_posting_group + ' · ' + row.gen_prod_posting_group,
			'Close',
			{ duration: 2500 },
		);
	}

	onCopySourceSelected(event: any): void {
		this.copyAccountsFrom(event?.option?.value);
	}

	get visibleAccountFields(): AccountField[] {
		return this.accountFields.filter((f) => f.group === this.activeGroup);
	}

	groupCount(group: 'sales' | 'purchase' | 'inventory'): number {
		return this.accountFields.filter((f) => f.group === group).length;
	}

	groupAssignedCount(group: 'sales' | 'purchase' | 'inventory'): number {
		return this.accountFields
			.filter((f) => f.group === group)
			.filter((f) => {
				const v = this.form.get(f.key)?.value;
				return v && String(v) !== '0';
			}).length;
	}

	toggleForm(): void {
		this.isFormVisible = !this.isFormVisible;
	}

	closeForm(): void {
		this.isFormVisible = false;
		this.resetForm();
	}

	filteredRows(): any[] {
		const q = (this.searchTerm || '').toLowerCase().trim();
		if (!q) {
			return this.rows;
		}
		return this.rows.filter((row) =>
			Object.values(row).some((value) => value != null && String(value).toLowerCase().includes(q)),
		);
	}

	loadGlCodes(): void {
		this.glService.fetchGeneralPostingSetupGlCodes().subscribe(
			(res: any) => {
				const list: GlOption[] = (res.glCodes || []).map((c: any) => {
					const code = String(c.code);
					const name = c.name || '';
					return { code, name, label: code + (name ? ' — ' + name : '') };
				});
				const none: GlOption = { code: '0', name: '— (none / default)', label: '0 — (none / default)' };
				this.glOptions = [none, ...list];
				this.glIndex = new Map(this.glOptions.map((o) => [o.code, o]));
				this.wireAutocompletes();
			},
			() => {
				const none: GlOption = { code: '0', name: '— (none / default)', label: '0 — (none / default)' };
				this.glOptions = [none];
				this.glIndex = new Map([[none.code, none]]);
				this.wireAutocompletes();
			},
		);
	}

	private wireAutocompletes(): void {
		for (const f of this.accountFields) {
			const ctrl = this.form.get(f.key);
			if (!ctrl) continue;
			this.filtered[f.key] = ctrl.valueChanges.pipe(
				startWith(ctrl.value || ''),
				map((value) => this.filterGl(value)),
			);
		}
	}

	private filterGl(value: any): GlOption[] {
		if (!this.glOptions.length) return [];
		if (value == null || value === '') return this.glOptions.slice(0, 50);
		const term = String(value).toLowerCase();
		const matches = this.glOptions.filter((o) =>
			o.code.toLowerCase().includes(term) || o.name.toLowerCase().includes(term),
		);
		return matches.slice(0, 50);
	}

	displayGl = (code: string): string => {
		if (!code) return '';
		const opt = this.glIndex.get(String(code));
		return opt ? opt.label : String(code);
	};

	glLabel(code: any): string {
		if (code == null || code === '') return '';
		const opt = this.glIndex.get(String(code));
		return opt ? opt.label : String(code);
	}

	loadRows(): void {
		this.isLoading = true;
		this.glService.fetchGeneralPostingSetup({}).subscribe(
			(response: any) => {
				this.rows = response.data || [];
				this.isLoading = false;
			},
			() => {
				this.snackBar.open('Error loading general posting setup.', 'Close', { duration: 4000 });
				this.rows = [];
				this.isLoading = false;
			},
		);
	}

	resetForm(): void {
		this.editingRow = null;
		this.activeGroup = 'sales';
		this.lastCopiedFrom = null;
		this.copySourceCtrl.setValue('', { emitEvent: false });
		this.form.reset({
			gen_bus_posting_group: '',
			gen_prod_posting_group: '',
			description: '',
			blocked: false,
		});
		for (const f of this.accountFields) {
			this.form.get(f.key)?.setValue('0');
		}
		this.form.get('gen_bus_posting_group')?.enable();
		this.form.get('gen_prod_posting_group')?.enable();
	}

	openNew(): void {
		this.resetForm();
		this.isFormVisible = true;
	}

	editRow(row: any): void {
		this.editingRow = row;
		const blocked = row.blocked === 1 || row.blocked === true || row.blocked === '1';
		const patch: any = {
			gen_bus_posting_group: row.gen_bus_posting_group,
			gen_prod_posting_group: row.gen_prod_posting_group,
			description: row.description || '',
			blocked,
		};
		for (const f of this.accountFields) {
			const v = row[f.key];
			patch[f.key] = v != null && String(v) !== '' ? String(v) : '0';
		}
		this.form.patchValue(patch);
		this.form.get('gen_bus_posting_group')?.disable();
		this.form.get('gen_prod_posting_group')?.disable();
		this.activeGroup = 'sales';
		this.isFormVisible = true;
	}

	cloneRow(row: any): void {
		this.resetForm();
		this.copyAccountsFrom(row);
		this.form.patchValue({
			description: row.description ? row.description + ' (copy)' : '',
			blocked: false,
		});
		this.activeGroup = 'sales';
		this.isFormVisible = true;
	}

	save(): void {
		const raw = this.form.getRawValue();
		if (this.form.invalid) {
			this.globalSearchService.getFormValidationErrors(this.form);
			this.snackBar.open('Please complete required fields.', 'Close', { duration: 3500 });
			return;
		}
		const payload: any = {
			...raw,
			blocked: raw.blocked ? 1 : 0,
		};
		if (this.editingRow) {
			payload.orig_gen_bus_posting_group = this.editingRow.gen_bus_posting_group;
			payload.orig_gen_prod_posting_group = this.editingRow.gen_prod_posting_group;
		}
		const req = this.editingRow
			? this.glService.updateGeneralPostingSetup(payload)
			: this.glService.addGeneralPostingSetup(payload);
		req.subscribe(
			(r: any) => {
				if (r.error) {
					this.snackBar.open(r.error, 'Close', { duration: 5000 });
					return;
				}
				this.snackBar.open(r.message || 'Saved.', 'Close', { duration: 2500 });
				this.isFormVisible = false;
				this.resetForm();
				this.loadRows();
			},
			() => this.snackBar.open('Save failed.', 'Close', { duration: 4000 }),
		);
	}

	deleteRow(row: any): void {
		if (!confirm('Delete this general posting setup row?')) {
			return;
		}
		this.glService
			.deleteGeneralPostingSetup({
				gen_bus_posting_group: row.gen_bus_posting_group,
				gen_prod_posting_group: row.gen_prod_posting_group,
			})
			.subscribe(
				() => {
					this.snackBar.open('Deleted.', 'Close', { duration: 2500 });
					this.loadRows();
				},
				() => this.snackBar.open('Delete failed.', 'Close', { duration: 4000 }),
			);
	}

	back(): void {
		this.location.back();
	}

	exportExcel(): void {
		const list = this.filteredRows();
		if (!list.length) {
			return;
		}
		const keys = [
			'gen_bus_posting_group',
			'gen_prod_posting_group',
			'description',
			'blocked',
			...this.accountFields.map((f) => f.key),
		];
		const rows = list.map((p) => {
			const o: Record<string, unknown> = {};
			for (const k of keys) {
				o[k] = p[k];
			}
			return o;
		});
		const title = 'General Posting Setup';
		const subtitle = 'Exported: ' + new Date().toLocaleString();
		this.globalSearchService.exportJsonToExcelWithTitle(
			title,
			subtitle,
			rows,
			'GeneralPostingSetup_' + new Date().toISOString().slice(0, 10),
		);
	}

	trackByKey = (_: number, item: AccountField): string => item.key;
	trackByCode = (_: number, item: GlOption): string => item.code;
	trackByRow = (_: number, item: any): string =>
		(item?.gen_bus_posting_group || '') + '|' + (item?.gen_prod_posting_group || '');
}
